import React from 'react';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { StatCard } from '../../../components/dashboard/widgets/StatCard';
import { ActivityFeed, ActivityItem } from '../../../components/dashboard/widgets/ActivityFeed';
import { ScheduleWidget, ScheduleItem } from '../../../components/dashboard/widgets/ScheduleWidget';
import { TaskWidget, TaskItem } from '../../../components/dashboard/widgets/TaskWidget';
import { QuickActionsWidget, QuickActionItem } from '../../../components/dashboard/widgets/QuickActionsWidget';
import { ClientPortfolioWidget } from '../../../components/dashboard/widgets/ClientPortfolioWidget';
import { ClientsNeedingAttention } from '../../../components/portal/ClientsNeedingAttention';
import {
  UserGroupIcon,
  DocumentTextIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  UserPlusIcon,
  DocumentPlusIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

export const StaffDashboard: React.FC = () => {
  const { userProfile } = useAuth();

  // Mock data for staff metrics
  const clientDistribution = [
    { label: 'Individual', value: 45, color: '#003DA5' },
    { label: 'Corporate', value: 25, color: '#2A78BD' },
    { label: 'Trust', value: 18, color: '#10B981' },
    { label: 'Estate', value: 12, color: '#F59E0B' }
  ];

  const todaySchedule: ScheduleItem[] = [
    {
      time: '9:00 AM',
      title: 'Portfolio Review - Smith Family',
      type: 'meeting',
      duration: '1 hour'
    },
    {
      time: '11:00 AM',
      title: 'New Client Onboarding - Tech Corp',
      type: 'onboarding',
      duration: '90 min'
    },
    {
      time: '2:00 PM',
      title: 'Tax Planning Call - Johnson Trust',
      type: 'call',
      duration: '45 min'
    },
    {
      time: '4:00 PM',
      title: 'Team Sync',
      type: 'internal',
      duration: '30 min'
    }
  ];

  const recentActivity: ActivityItem[] = [
    {
      id: '1',
      type: 'client',
      title: 'New client onboarded',
      description: 'Tech Corp - Corporate account',
      timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
      status: 'success'
    },
    {
      id: '2',
      type: 'document',
      title: 'Investment proposal submitted',
      description: 'Johnson Trust - Q1 2025 strategy',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
      user: 'You'
    },
    {
      id: '3',
      type: 'message',
      title: 'Client inquiry received',
      description: 'Smith Family - Estate planning questions',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000)
    },
    {
      id: '4',
      type: 'task',
      title: 'Compliance review completed',
      description: 'Q4 2024 client portfolios reviewed',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      status: 'success'
    },
    {
      id: '5',
      type: 'meeting',
      title: 'Annual review scheduled',
      description: 'Brown Foundation - February 15',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
    }
  ];

  const clientTasks: TaskItem[] = [
    { id: '1', client: 'Smith Family', task: 'Review insurance policies', due: 'Today', priority: 'high' },
    { id: '2', client: 'Tech Corp', task: 'Prepare Q1 investment strategy', due: 'Tomorrow', priority: 'medium' },
    { id: '3', client: 'Johnson Trust', task: 'Tax optimization report', due: 'This week', priority: 'high' },
    { id: '4', client: 'Brown Foundation', task: 'Annual compliance check', due: 'Next week', priority: 'low' }
  ];

  const quickActions: QuickActionItem[] = [
    { label: 'Add Client', icon: UserPlusIcon, href: '/portal/clients/new' },
    {
      label: 'Schedule Meeting', icon: CalendarIcon, onClick: () => {
        if (import.meta.env.DEV) {
          console.log('Schedule');
        }
      }
    },
    {
      label: 'Upload Document', icon: DocumentPlusIcon, onClick: () => {
        if (import.meta.env.DEV) {
          console.log('Upload');
        }
      }
    },
    { label: 'Send Message', icon: ChatBubbleLeftRightIcon, href: '/portal/messages/new' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Message */}
      <div className="mb-6">
        <p className="text-lg text-gray-600">
          Welcome back, {userProfile?.full_name} - {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Active Clients"
          value="68"
          change={{ value: '+3 this month', isPositive: true }}
          icon={UserGroupIcon}
          iconBgColor="bg-blue-500"
        />
        <StatCard
          title="Meetings Today"
          value="4"
          icon={CalendarIcon}
          iconBgColor="bg-green-500"
        />
        <StatCard
          title="Pending Tasks"
          value="12"
          change={{ value: '3 due today', isPositive: false }}
          icon={ClipboardDocumentListIcon}
          iconBgColor="bg-amber-500"
        />
        <StatCard
          title="Documents"
          value="247"
          change={{ value: '+18 this week', isPositive: true }}
          icon={DocumentTextIcon}
          iconBgColor="bg-purple-500"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Today's Schedule */}
          <ScheduleWidget schedule={todaySchedule} />

          {/* Client Tasks */}
          <TaskWidget tasks={clientTasks} />

          {/* Recent Activity */}
          <ActivityFeed
            activities={recentActivity}
            maxItems={5}
            showViewAll={true}
            onViewAll={() => {
              if (import.meta.env.DEV) {
                console.log('View all activity');
              }
            }}
          />
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Quick Actions */}
          <QuickActionsWidget actions={quickActions} />

          {/* Clients Needing Attention */}
          <ClientsNeedingAttention />

          {/* Client Distribution */}
          <ClientPortfolioWidget data={clientDistribution} />

          {/* Performance Summary */}
          <div className="bg-white portal-content-shadow rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Performance</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Client Satisfaction</span>
                <span className="text-sm font-medium text-green-600">98%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Response Time</span>
                <span className="text-sm font-medium text-gray-900">&lt; 2 hours</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Tasks Completed</span>
                <span className="text-sm font-medium text-gray-900">47 this month</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">New Clients</span>
                <span className="text-sm font-medium text-blue-600">+3</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};